import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { TimeSheetReportsPort } from '../../../app/ports/reports/time-sheet-reports.port';

import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';

import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';
import { TimeSheetSummaryRequest } from '../../../domain/models/reports/request/time-sheet-summary.request';
import { TimeSheetSummaryResponse } from '../../../domain/models/reports/time-sheet-summary.model';
import { mapSdkTimeSheetSummaryResponseToDomain } from '../../mappers/reports/time-sheet-reports.mapper';
import { timeSheetReportsSdkMock } from '../../mocks/report/time-sheet-reports.mock';

type TimeSheetReportsMockAction = 'getTimeSheet';

@Injectable({ providedIn: 'root' })
export class TimeSheetReportsPortAdapter implements TimeSheetReportsPort {
    constructor(
        private timeSheetReportsService: ReportSdk.TimeSheetReportsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getTimeSheet(payload: TimeSheetSummaryRequest): Observable<TimeSheetSummaryResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-timesheet');

        const sdkPayload: ReportSdk.TimeSheetSummaryRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.page !== undefined ? { page: payload.page } : {}),
            ...(payload.size !== undefined ? { size: payload.size } : {}),
        };

        const source$: Observable<ReportSdk.TimeSheetSummaryResponse> = this.shouldMock('getTimeSheet')
            ? of(
                timeSheetReportsSdkMock.getTimeSheet(
                    sdkPayload.employeeId,
                    sdkPayload.dateRange,
                    sdkPayload.page ?? 0,
                    sdkPayload.size ?? 20,
                ),
            ).pipe(delay(360))
            : this.timeSheetReportsService.getTimeSheet(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkTimeSheetSummaryResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-timesheet')),
        );
    }

    private shouldMock(action: TimeSheetReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.timeSheetReports?.[action]);
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
